import { withRetry, type RetryOptions, isRetryableError } from '../../utils/retry';

export type ProviderResult<T> = {
  data: T;
  provider: string;
  latencyMs?: number;
};

export type ProviderInvoker<T> = {
  name: string;
  invoke: () => Promise<ProviderResult<T> | null>;
  timeoutMs?: number;
  /** 单个 provider 的重试配置（默认不重试） */
  retry?: RetryOptions;
};

type OrchestratorOptions = {
  totalTimeoutMs?: number;
  logger?: (msg: string, extra?: unknown) => void;
  /** 全局重试配置，应用于所有 provider（除非 provider 自己指定） */
  defaultRetry?: RetryOptions;
};

const withTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('timeout')), ms);
    promise
      .then((val) => {
        clearTimeout(id);
        resolve(val);
      })
      .catch((err) => {
        clearTimeout(id);
        reject(err);
      });
  });
};

/**
 * Runs providers sequentially until one succeeds (non-null result).
 * Each provider and the whole chain support timeouts to avoid hanging UI.
 */
export class LLMOrchestrator<T> {
  private readonly providers: ProviderInvoker<T>[];
  private readonly totalTimeoutMs: number | undefined;
  private readonly logger: (msg: string, extra?: unknown) => void;
  private readonly defaultRetry: RetryOptions | undefined;

  constructor(providers: ProviderInvoker<T>[], opts?: OrchestratorOptions) {
    this.providers = providers;
    this.totalTimeoutMs = opts?.totalTimeoutMs;
    this.logger = opts?.logger ?? (() => {});
    this.defaultRetry = opts?.defaultRetry;
  }

  async run(): Promise<ProviderResult<T>> {
    const wrapped = this.totalTimeoutMs
      ? withTimeout(this.runInternal(), this.totalTimeoutMs)
      : this.runInternal();

    return wrapped;
  }

  private async runInternal(): Promise<ProviderResult<T>> {
    for (const p of this.providers) {
      const started = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      const retryOpts = p.retry ?? this.defaultRetry;

      try {
        // 包装 invoke 调用，支持重试
        const invokeWithRetry = async () => {
          const maybePromise = p.invoke();
          return p.timeoutMs ? await withTimeout(maybePromise, p.timeoutMs) : await maybePromise;
        };

        const res = retryOpts
          ? await withRetry(invokeWithRetry, {
              ...retryOpts,
              onRetry: (err, attempt, delayMs) => {
                this.logger(`[llm] provider ${p.name} retry ${attempt}`, { err, delayMs });
                retryOpts.onRetry?.(err, attempt, delayMs);
              },
            })
          : await invokeWithRetry();

        if (res) {
          const ended = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
          const final = {
            ...res,
            latencyMs: res.latencyMs ?? Math.round(ended - started),
            provider: res.provider ?? p.name,
          };
          this.logger('[llm] provider success', { provider: final.provider, latencyMs: final.latencyMs });
          return final;
        }
      } catch (err) {
        this.logger(`[llm] provider ${p.name} failed`, err);
      }
    }
    throw new Error('No LLM provider available');
  }
}
